import { styled } from "goober";
import { type ReactNode, useState } from "react";
import { useVolumeDirectory } from "../../hooks/useVolumeDirectory";
import { volumeFilePath } from "../../lib/apiPaths";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import { FilePreviewPanel } from "../AttachmentHoverPreview";
import { ButtonPress } from "../Button/ButtonPress";
import { EmptyState } from "../EmptyState";
import { FileList, FileListRow } from "../FileList";
import { PanelTransition } from "../PanelTransition";
import type { FilePreviewTarget } from "../FileRender";
import { WavyLine } from "../WavyLine";
import { StorageDeleteConfirm } from "./StorageDeleteConfirm";
import type { StorageFileTarget } from "./storageUsageLevel";
import { useVolumeListingContext, volumeListing } from "./volumeListing";

const Panel = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export type StorageHeaderParts = {
  count?: number;
  // Listings carry no folder sizes, so only a volume’s top folder shows one.
  showTotal?: boolean;
  action?: ReactNode;
};

const loadingState = (
  <EmptyState
    icon={<WavyLine size={16} opacity={0.4} />}
    title="Loading files…"
  />
);

// The listing lives here rather than in the sliding list, so the header’s count
// stays put while folders slide in.
export function StorageFiles({
  target,
  canDelete,
  header,
}: {
  target: StorageFileTarget;
  canDelete: boolean;
  header: (parts?: StorageHeaderParts) => ReactNode;
}) {
  const { volumes, personalSpaceId, loading } = useVolumeListingContext(
    target.spaceId
  );
  const { pathBase } = volumeListing(target, volumes, personalSpaceId);

  if (loading || !pathBase) {
    return (
      <Panel>
        {header()}
        <FileList title={target.volumeName} showHeader={false}>
          {loading ? (
            loadingState
          ) : (
            <EmptyState
              iconName="folder"
              title="Files can’t be listed"
              description={
                target.category === "personal_volume"
                  ? "Only its owner can browse a personal volume’s files."
                  : "This volume isn’t bound to a published workflow, so its files can’t be listed."
              }
            />
          )}
        </FileList>
      </Panel>
    );
  }
  return (
    <VolumeFiles
      key={pathBase}
      target={target}
      pathBase={pathBase}
      canDelete={canDelete}
      header={header}
    />
  );
}

function VolumeFiles({
  target,
  pathBase,
  canDelete,
  header,
}: {
  target: StorageFileTarget;
  pathBase: string;
  canDelete: boolean;
  header: (parts?: StorageHeaderParts) => ReactNode;
}) {
  const { path, dir: storageDir } = useCurrentUrl(routes.modal.storageUsage);
  const dirs = storageDir ? storageDir.split("/") : [];
  const { entries, loading, error } = useVolumeDirectory(
    pathBase,
    dirs.join("/")
  );
  // The listing isn’t live, so deleted entries stay hidden until it’s reopened.
  const [deleted, setDeleted] = useState<ReadonlySet<string>>(new Set());
  const [deleting, setDeleting] = useState<{
    path?: string;
    onDeleted: () => void;
  } | null>(null);
  const [previewing, setPreviewing] = useState<{
    file: FilePreviewTarget;
    // Set only for a file opened from the listing, which is all that can be deleted.
    path: string | null;
  } | null>(null);
  const title = dirs.at(-1) ?? target.volumeName;
  const directoryKey = dirs.join("/");
  const openDir = (dirPath: string[]) =>
    navigate(routes.modal.storageUsage, { path, dir: dirPath.join("/") });
  const pathOf = (name: string) => [...dirs, name].join("/");
  const hide = (...paths: string[]) =>
    setDeleted((prev) => new Set([...prev, ...paths]));
  const sorted = entries
    .filter((entry) => !deleted.has(pathOf(entry.name)))
    .toSorted(
      (a, b) =>
        Number(b.kind === "directory") - Number(a.kind === "directory") ||
        (a.kind === "directory" ? 0 : b.size - a.size) ||
        a.name.localeCompare(b.name)
    );

  return (
    <Panel>
      {/* An open file’s own header, with its back button and file actions, replaces this one. */}
      {!previewing &&
        header({
          count: loading || error ? undefined : sorted.length,
          showTotal: dirs.length === 0,
          action: canDelete && (
            <ButtonPress
              variant="secondary"
              icon="trash"
              aria-label={
                dirs.length === 0 ? "Delete volume data" : `Delete ${title}`
              }
              title={dirs.length === 0 ? "Delete volume data" : "Delete folder"}
              onPress={() =>
                setDeleting(
                  dirs.length === 0
                    ? {
                        onDeleted: () =>
                          hide(...entries.map((entry) => pathOf(entry.name))),
                      }
                    : {
                        path: directoryKey,
                        onDeleted: () => {
                          hide(directoryKey);
                          openDir(dirs.slice(0, -1));
                        },
                      }
                )
              }
            />
          ),
        })}
      <PanelTransition
        viewKey={previewing?.file.url ?? directoryKey}
        depth={dirs.length + (previewing ? 1 : 0)}
      >
        {previewing ? (
          <FilePreviewPanel
            key={previewing.file.url}
            embedded
            interactive
            {...previewing.file}
            onBack={() => setPreviewing(null)}
            backLabel={`Back to ${title}`}
            onOpenFile={(file) => setPreviewing({ file, path: null })}
            onDelete={
              canDelete && previewing.path !== null
                ? () => {
                    const filePath = previewing.path!;
                    setDeleting({
                      path: filePath,
                      onDeleted: () => {
                        hide(filePath);
                        setPreviewing(null);
                      },
                    });
                  }
                : undefined
            }
          />
        ) : (
          <FileList key={directoryKey} title={title} showHeader={false}>
            {loading ? (
              loadingState
            ) : error ? (
              <EmptyState
                iconName="traffic-cone"
                title="Couldn’t load these files"
                description={error}
              />
            ) : sorted.length === 0 ? (
              <EmptyState iconName="folder" title="This folder is empty" />
            ) : (
              sorted.map((entry) => {
                const folder = entry.kind === "directory";
                const url = volumeFilePath(pathBase, pathOf(entry.name));
                return (
                  <FileListRow
                    key={entry.name}
                    name={entry.name}
                    folder={folder}
                    sizeBytes={entry.size}
                    thumbnailUrl={url}
                    onPress={() =>
                      folder
                        ? openDir([...dirs, entry.name])
                        : setPreviewing({
                            file: {
                              url,
                              fileName: entry.name,
                              sizeBytes: entry.size,
                            },
                            path: pathOf(entry.name),
                          })
                    }
                  />
                );
              })
            )}
          </FileList>
        )}
      </PanelTransition>
      {deleting && (
        <StorageDeleteConfirm
          deletion={{ target, path: deleting.path }}
          onClose={() => setDeleting(null)}
          onDeleted={deleting.onDeleted}
        />
      )}
    </Panel>
  );
}
